import { Link } from '@tanstack/react-router';
import type { LostBonusOffer } from '@/domain/lost-bonuses';
import { cn } from '@/lib/cn';
import { Badge, Button, EmptyState, Spinner, Toast, buttonVariants } from '@/ui/common';
import { ClaimBonusOfferCard } from './ClaimBonusOfferCard';
import { ClaimBonusSuccess } from './ClaimBonusSuccess';

export interface ClaimBonusPanelProps {
  offers: LostBonusOffer[];
  selectedId: string | null;
  onSelect: (offer: LostBonusOffer) => void;
  onConfirm: () => void;
  isLoading: boolean;
  isConfirming: boolean;
  claimed: boolean;
  listError: Error | null;
  claimErrorKey: string | null;
  onDismissClaimError?: () => void;
  label: (key: string, fallback: string) => string;
  className?: string;
}

/**
 * Claim Bonus page body: intro, selectable offers, Confirm CTA (Figma ClaimBonusPanel).
 */
export function ClaimBonusPanel({
  offers,
  selectedId,
  onSelect,
  onConfirm,
  isLoading,
  isConfirming,
  claimed,
  listError,
  claimErrorKey,
  onDismissClaimError,
  label,
  className,
}: ClaimBonusPanelProps) {
  if (claimed) {
    return <ClaimBonusSuccess label={label} className={className} />;
  }

  if (isLoading) {
    return (
      <div className={cn('flex justify-center py-16', className)}>
        <Spinner size="lg" />
      </div>
    );
  }

  if (listError) {
    return (
      <div className={cn('flex flex-col gap-3', className)}>
        <Toast tone="danger">
          {listError.message || label('SomethingWentWrong', 'Something went wrong.')}
        </Toast>
      </div>
    );
  }

  if (offers.length === 0) {
    return (
      <EmptyState
        className={className}
        icon="bonus-gift"
        title={label('NoBonusOffers', 'No Bonus Offers?')}
        description={label(
          'DepositNowToUnlockNewOffers',
          'Deposit Now to Unlock New Offers',
        )}
        action={
          <Link
            to="/profile/deposits"
            className={buttonVariants({ variant: 'primary', size: 'md' })}
          >
            {label('DepositNow', 'Deposit Now')}
          </Link>
        }
      />
    );
  }

  return (
    <div className={cn('flex w-full flex-col gap-7', className)}>
      {claimErrorKey ? (
        <Toast tone="danger" onDismiss={onDismissClaimError}>
          {label(claimErrorKey, claimErrorKey)}
        </Toast>
      ) : null}

      <div className="flex flex-col gap-2.5 rounded-md bg-bg-elevated p-5">
        <Badge tone="success" size="md">
          {label('Available now', 'Available now')}
        </Badge>
        <p className="text-[22px] font-bold text-text-primary">
          {label('Claim your lost bonus', 'Claim your lost bonus')}
        </p>
        <p className="text-[14px] text-text-secondary">
          {label(
            'Pick one eligible offer below. Rewards credit after you confirm — choose the product that fits how you play.',
            'Pick one eligible offer below. Rewards credit after you confirm — choose the product that fits how you play.',
          )}
        </p>
      </div>

      <div
        role="radiogroup"
        aria-label={label('ClaimBonus', 'Claim bonus')}
        className="flex flex-col gap-3.5"
      >
        {offers.map((offer) => (
          <ClaimBonusOfferCard
            key={offer.Id}
            offer={offer}
            selected={selectedId === offer.Id}
            onSelect={() => onSelect(offer)}
            label={label}
          />
        ))}
      </div>

      <div className="flex flex-col gap-2.5">
        <Button
          type="button"
          variant="primary"
          size="lg"
          fullWidth
          loading={isConfirming}
          onClick={onConfirm}
        >
          {label('Confirm claim', 'Confirm claim')}
        </Button>
        <p className="text-[12px] text-text-secondary">
          {label(
            'Only one lost bonus can be claimed per eligibility window. Wagering and product rules apply.',
            'Only one lost bonus can be claimed per eligibility window. Wagering and product rules apply.',
          )}
        </p>
      </div>
    </div>
  );
}
